/**
 * Icônes Lucide en SVG inline — Editorial Operations (HiFi.2).
 *
 * Re-implémentation lit-html du jeu d'icônes Lucide utilisé dans le
 * handoff Editorial (`icons.jsx`). Stroke 1.5 par défaut, linecap/join
 * round, viewBox 24×24. Couleur héritée via `currentColor` — il suffit
 * de positionner `color` sur le parent pour teinter l'icône.
 *
 * Pourquoi pas le package npm `lucide` ?
 *   - Pas de dépendance externe (philosophie vanilla, cf. ADR 0001) ;
 *   - On n'a besoin que d'une vingtaine d'icônes — quelques centaines
 *     d'octets vs ~3 KB gzip pour le tree-shake `lucide`.
 *   - Paths fidèles au handoff design : si Lucide upstream change un
 *     glyphe (ce qui est arrivé), on n'est pas surpris.
 *
 * Usage typique :
 *   import { Icon, Icons } from '../../ui/icons.js';
 *   html`<button>${Icon(Icons.menu, 20)} Menu</button>`
 *
 * Pour customiser stroke / style, passer les options :
 *   ${Icon(Icons.home, 20, { stroke: 1.8, style: 'color: var(--accent-deep)' })}
 */
import { html, svg, type SVGTemplateResult, type TemplateResult } from 'lit-html';

/** Définition d'une icône — paths SVG raw (avant inclusion dans `<svg>`). */
export type IconShape = SVGTemplateResult;

export interface IconOptions {
  /** Taille en px (carré). Default 18. */
  size?: number;
  /** Épaisseur de trait (1.5 par défaut, 1.8 pour les états actifs). */
  stroke?: number;
  /** Style inline complémentaire (couleur, etc.). */
  style?: string;
  /** Label aria — laisser vide pour `aria-hidden`. */
  ariaLabel?: string;
}

/**
 * Wrap des paths Lucide dans un `<svg>` 24×24 standard. Toujours
 * `fill="none"` (Lucide pur stroke). `currentColor` propage `color`
 * depuis le parent.
 */
export function Icon(shape: IconShape, size = 18, options: IconOptions = {}): TemplateResult {
  const stroke = options.stroke ?? 1.5;
  const style = options.style ?? '';
  const ariaLabel = options.ariaLabel;
  return html`<svg
    width="${size}"
    height="${size}"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="${stroke}"
    stroke-linecap="round"
    stroke-linejoin="round"
    style="${style}"
    aria-hidden="${ariaLabel ? 'false' : 'true'}"
    role="${ariaLabel ? 'img' : 'presentation'}"
    aria-label="${ariaLabel ?? ''}"
  >
    ${shape}
  </svg>`;
}

/**
 * Catalogue des shapes Lucide utilisés dans Nestor. Ajouter au fil
 * de l'eau, mais éviter d'importer le pack `lucide` complet — chaque
 * icône doit avoir une raison d'être (cf. CLAUDE.md §3.7).
 */
export const Icons = {
  home: svg`<path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" />`,
  calendar: svg`<rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" />`,
  check: svg`<path d="M5 12l5 5L20 7" />`,
  checkbox: svg`<rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 12l3 3 5-6" />`,
  square: svg`<rect x="4" y="4" width="16" height="16" rx="3" />`,
  chef: svg`<path d="M6 14a4 4 0 1 1 2-7.5 4 4 0 0 1 8 0A4 4 0 1 1 18 14" /><path d="M6 14h12v5H6z" />`,
  image: svg`<rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M21 17l-5-5-9 9" />`,
  message: svg`<path d="M21 12a8 8 0 0 1-12.3 6.7L3 20l1.4-5A8 8 0 1 1 21 12z" />`,
  users: svg`<circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><circle cx="17" cy="9" r="2.5" /><path d="M21 19a4.5 4.5 0 0 0-6-4" />`,
  bag: svg`<path d="M5 8h14l-1 12H6z" /><path d="M9 8a3 3 0 0 1 6 0" />`,
  school: svg`<path d="M22 10 12 5 2 10l10 5 10-5Z" /><path d="M6 12.5V17c0 1.7 2.7 3 6 3s6-1.3 6-3v-4.5" />`,
  book: svg`<path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2z" /><path d="M4 5v16" />`,
  euro: svg`<path d="M17 7a6 6 0 1 0 0 10" /><path d="M5 11h9M5 14h9" />`,
  bell: svg`<path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2.5h-15z" /><path d="M10 20a2 2 0 0 0 4 0" />`,
  search: svg`<circle cx="11" cy="11" r="6.5" /><path d="m20 20-3.5-3.5" />`,
  sliders: svg`<path d="M4 6h12M4 12h8M4 18h14" /><circle cx="18" cy="6" r="2" /><circle cx="14" cy="12" r="2" /><circle cx="20" cy="18" r="2" />`,
  plus: svg`<path d="M12 5v14M5 12h14" />`,
  chevronRight: svg`<path d="m9 6 6 6-6 6" />`,
  chevronLeft: svg`<path d="m15 6-6 6 6 6" />`,
  chevronDown: svg`<path d="m6 9 6 6 6-6" />`,
  chevronUp: svg`<path d="m18 15-6-6-6 6" />`,
  more: svg`<circle cx="6" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="18" cy="12" r="1" />`,
  menu: svg`<path d="M4 7h16M4 12h16M4 17h16" />`,
  x: svg`<path d="M6 6l12 12M18 6L6 18" />`,
  arrowRight: svg`<path d="M5 12h14M13 5l7 7-7 7" />`,
  send: svg`<path d="M22 2 11 13" /><path d="M22 2l-7 20-4-9-9-4z" />`,
  paperclip: svg`<path d="M21 11.5l-9 9a5 5 0 1 1-7-7l9-9a3.5 3.5 0 1 1 5 5l-9 9a2 2 0 1 1-3-3l8-8" />`,
  spark: svg`<path d="M12 4v4M12 16v4M4 12h4M16 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M6.3 17.7l2.8-2.8M14.9 9.1l2.8-2.8" />`,
  receipt: svg`<path d="M5 3h14v18l-3-2-3 2-3-2-3 2-2-2z" /><path d="M9 8h6M9 12h6M9 16h4" />`,
  music: svg`<path d="M9 18V6l11-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" />`,
  cart: svg`<path d="M3 4h2l2 12h12l2-9H7" /><circle cx="9" cy="20" r="1.5" /><circle cx="17" cy="20" r="1.5" />`,
  settings: svg`<circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />`,
  /** Étoile éditoriale décorative pour le bloc AiSuggestion. */
  starEditorial: svg`<path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />`,

  // ── Lot 8 (audit D2) : zéro emoji UI — actions & catégories ────────
  pencil: svg`<path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />`,
  trash: svg`<path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />`,
  share: svg`<path d="M4 12v8h16v-8" /><path d="M16 6l-4-4-4 4M12 2v13" />`,
  mic: svg`<rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 19v3" />`,
  eye: svg`<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />`,
  camera: svg`<path d="M4 8h3l2-3h6l2 3h3v12H4z" /><circle cx="12" cy="13" r="3.5" />`,
  refresh: svg`<path d="M21 12a9 9 0 1 1-2.6-6.3M21 3v6h-6" />`,
  mail: svg`<rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" />`,
  alert: svg`<path d="M12 3l10 18H2z" /><path d="M12 10v5M12 18.5v.1" />`,
  box: svg`<path d="M3 8l9-5 9 5v8l-9 5-9-5z" /><path d="M3 8l9 5 9-5M12 13v9" />`,
  ball: svg`<circle cx="12" cy="12" r="9" /><path d="M12 3v4l-4 3 1.5 5h5L18 10l-4-3V3M5 18l3-3M19 18l-3-3" />`,
  shirt: svg`<path d="M8 3l4 2 4-2 5 4-3 3-1-1v12H7V9L6 10 3 7z" />`,
  gift: svg`<rect x="3" y="8" width="18" height="4" /><path d="M5 12v9h14v-9M12 8v13" /><path d="M12 8s-4 0-5-2 1-4 3-2 2 4 2 4zM12 8s4 0 5-2-1-4-3-2-2 4-2 4z" />`,
  health: svg`<path d="M9 3h6v6h6v6h-6v6H9v-6H3V9h6z" />`,
  car: svg`<path d="M5 16l1.5-6h11L19 16M3 16h18v4h-2a1.5 1.5 0 0 1-3 0H8a1.5 1.5 0 0 1-3 0H3z" />`,
  utensils: svg`<path d="M7 3v7a2 2 0 0 0 4 0V3M9 3v18" /><path d="M17 3c-1.5 1-2 3-2 5s.5 4 2 4v9" />`,
  // Météo (widget accueil — même direction filaire).
  sun: svg`<circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />`,
  cloudSun: svg`<path d="M12 2v2M4.9 4.9l1.4 1.4M2 12h2M19 5l-1.4 1.4" /><path d="M15 13a4 4 0 0 0-7.6-1.6A3.5 3.5 0 0 0 7.5 18H15a2.9 2.9 0 0 0 0-5.8z" />`,
  cloud: svg`<path d="M17 18H7a4 4 0 0 1-.6-8A5.5 5.5 0 0 1 17 8.6 4.2 4.2 0 0 1 17 18z" />`,
  fog: svg`<path d="M4 14h16M6 18h12M8 22h8" /><path d="M17 10a4.2 4.2 0 0 0 0-1.4A5.5 5.5 0 0 0 6.4 6 4 4 0 0 0 4 10" />`,
  drizzle: svg`<path d="M17 14H7a4 4 0 0 1-.6-8A5.5 5.5 0 0 1 17 4.6 4.2 4.2 0 0 1 17 14z" /><path d="M8 18v1M12 18v1M16 18v1" />`,
  rain: svg`<path d="M17 13H7a4 4 0 0 1-.6-8A5.5 5.5 0 0 1 17 3.6 4.2 4.2 0 0 1 17 13z" /><path d="M8 16v4M12 17v4M16 16v4" />`,
  snow: svg`<path d="M17 13H7a4 4 0 0 1-.6-8A5.5 5.5 0 0 1 17 3.6 4.2 4.2 0 0 1 17 13z" /><path d="M8 17v.1M12 19v.1M16 17v.1M10 21v.1M14 21v.1" />`,
  storm: svg`<path d="M17 12H7a4 4 0 0 1-.6-8A5.5 5.5 0 0 1 17 2.6 4.2 4.2 0 0 1 17 12z" /><path d="M13 12l-3 5h4l-3 5" />`,
} as const;

export type IconName = keyof typeof Icons;
